import { A } from '@adornis/base/env-info.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { assembleMailTemplate, replaceVariables } from '@adornis/mails/db/mail-template.js';
import type { BuildEmailOptions } from '@adornis/mails/db/templates.js';
import { html, nothing } from 'lit';
import { getLegacyGlobalSetting } from '../api/global-settings.js';
import { getGlobalSettings } from '../api/settings.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import {
  type GlobalMailTemplate,
  type InstitutionMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from '../entities/mail-templates.js';

export async function assembleGlobalMail(options: {
  template: GlobalMailTemplate;
  infos?: Record<string, string | undefined>;
  overrides?: Partial<BuildEmailOptions>;
}): Promise<{ subject: string; content: Renderable }> {
  const { template, infos, overrides } = options;
  const globalSettings = await getLegacyGlobalSetting()({ emailBannerImageID: 1 });
  const newGlobalSetting = await getGlobalSettings()({
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
  });

  return assembleMailTemplate({
    template: {
      ...template.toJSON(),
      subject: replaceVariables(infos, legacyVariables(template.emailType), template.subject),
      content: replaceVariables(infos, legacyVariables(template.emailType), template.content),
    },
    // The legacy renderer uses a fixed document title and ignores template logo/colour fields.
    overrides: {
      title: 'Buchungsheld Benachrichtigungsmail',
      footer: html``,
      logoConfig: globalSettings.emailBannerImageID
        ? { src: AdornisFile.getServeLink(globalSettings.emailBannerImageID), alt: 'Logo', width: 250 }
        : undefined,
      colorConfig: {
        mainBorder: 'none',
        mainBack: DesignSystem.currentTheme.getValue().colors.neutral.shade(50),
        wrapperBack: 'transparent',
        highlight:
          newGlobalSetting?.colorDefinitions?.accentColor ?? DesignSystem.currentTheme.getValue().colors.accent,
      },
      ...overrides,
    },
    // Empty bodies must remain absent, including when a caller supplies contents.
    ...(template.content ? {} : { renderFunction: () => '' }),
  });
}

export async function assembleInstitutionMail(options: {
  template: InstitutionMailTemplate;
  institution?: Institution;
  infos?: Record<string, string | undefined>;
  overrides?: Partial<BuildEmailOptions>;
}): Promise<{ subject: string; content: Renderable }> {
  const { template, infos } = options;
  const institution =
    options.institution ??
    template.institution ??
    (template.institutionID ? await Institution._getInstitutionByID(template.institutionID) : undefined);
  const params = institution ? new URLSearchParams({ institution: template.institutionID as string }) : undefined;
  const globalSettings = await getLegacyGlobalSetting()({ emailBannerImageID: 1 });
  const bannerID = institution?.emailBannerImageID || globalSettings.emailBannerImageID;

  // Institution rendering historically ignores caller overrides; retain that precedence.
  return assembleGlobalMail({
    template,
    infos,
    overrides: {
      footer: html`
        <div class="section" style="margin-top: 20px;">
          <div style="width: 100%">
            ${institution ? html` <p>${institution.name}</p> ` : nothing}
            <div>
              <a
                style="text-decoration: none; font-size: 0.9em !important;"
                href=${`${A.absoluteUrl()}impressum?${params?.toString()}`}
              >
                Impressum
              </a>
            </div>
            <div>
              <a
                style="text-decoration: none; font-size: 0.9em !important;"
                href=${`${A.absoluteUrl()}datenschutz?${params?.toString()}`}
              >
                Datenschutz
              </a>
            </div>
          </div>
        </div>
      `,
      ...(bannerID ? { logoConfig: { src: AdornisFile.getServeLink(bannerID), alt: 'Logo', width: 250 } } : {}),
      colorConfig: {
        mainBorder: 'none',
        mainBack: DesignSystem.currentTheme.getValue().colors.neutral.shade(50),
        wrapperBack: 'transparent',
        highlight: institution?.colorDefinitions?.accentColor,
      },
    },
  });
}

// Keep the entity renderer's variable whitelist and replacement order during coexistence.
// Some registered definitions (including SD invoices and plugin mail) differ from this list.
// This deliberately does not call the deprecated static entity API.
function legacyVariables(emailType: string): string[] {
  switch (emailType) {
    case MailTemplateType.ABMELDUNG:
    case MailTemplateType.ABMELDUNG_DURCH_EINRICHTUNG:
    case MailTemplateType.BESTAETIGUNG_WORKSHOP_REGISTRIERUNG:
    case MailTemplateType.NACHRUECKPLATZ_VERFUEGBAR:
    case MailTemplateType.NACHRUECKPLATZ_ERHALTEN:
    case MailTemplateType.NUTZER_TEILNAHME_AUFGEHOBEN:
    case MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN:
    case MailTemplateType.WARTELISTE_VORRUECK:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.CHANGED_REGISTRATIONS,
        MailTemplateVars.CHANGED_REGISTRATIONS_EN,
        MailTemplateVars.OTHER_REGISTRATIONS,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.EVENT_TITLE,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.EVENT_DATES,
        MailTemplateVars.EVENT_LEAD_DAYS,
        MailTemplateVars.NEWSLETTER_OPTIN_LINK,
        MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN,
      ];
    case MailTemplateType.NUTZER_TEILNAHME:
    case MailTemplateType.NUTZER_TEILNAHME_OHNE_ZERTIFIKAT:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.EVENT_TITLE,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.EVALUATION_LINK,
        MailTemplateVars.EVALUATION_CODE,
        MailTemplateVars.NEWSLETTER_OPTIN_LINK,
        MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN,
      ];
    case MailTemplateType.PRODUCT_CANCELED:
    case MailTemplateType.PRODUCT_DECANCELED:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.WORKSHOP_NAME,
        MailTemplateVars.WORKSHOP_TERMINE,
        MailTemplateVars.WORKSHOP_LOCATION,
      ];
    case MailTemplateType.NEWSLETTER_OPTIN:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.NEWSLETTER_OPTIN_LINK,
        MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN,
      ];

    case MailTemplateType.SPERRUNG:
    case MailTemplateType.SPERRUNG_AUFGEHOBEN:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.CHANGED_REGISTRATIONS,
        MailTemplateVars.CHANGED_REGISTRATIONS_EN,
      ];
    case MailTemplateType.ERINNERUNG:
    case MailTemplateType.ERINNERUNG_2:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.PRODUCT_CARD,
        MailTemplateVars.PRODUCT_CARD_EN,
        MailTemplateVars.AUTOMATIC_MOVE_UP_DAYS,
        MailTemplateVars.BLOCK_SIGNOFF_DAYS,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.DEREGISTER_LINK,
        MailTemplateVars.DEREGISTER_LINK_EN,
        MailTemplateVars.EVENT_LOCATION,
        MailTemplateVars.EVENT_DATES,
        MailTemplateVars.EVENT_LEAD_DAYS,
        MailTemplateVars.CHANGED_REGISTRATIONS,
        MailTemplateVars.CHANGED_REGISTRATIONS_EN,
        MailTemplateVars.EVALUATION_LINK,
        MailTemplateVars.EVALUATION_CODE,
        MailTemplateVars.MEETING_LINK,
        MailTemplateVars.MEETING_CODE,
        MailTemplateVars.MEETING_PASSWORD,
        MailTemplateVars.NEWSLETTER_OPTIN_LINK,
        MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN,
      ];
    case MailTemplateType.ERINNERUNG_APPOINTMENT:
    case MailTemplateType.ERINNERUNG_APPOINTMENT_2:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.PRODUCT_CARD,
        MailTemplateVars.PRODUCT_CARD_EN,
        MailTemplateVars.APPOINTMENT_NAME,
        MailTemplateVars.APPOINTMENT_START,
        MailTemplateVars.APPOINTMENT_END,
        MailTemplateVars.APPOINTMENT_LOCATION,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.DEREGISTER_LINK,
        MailTemplateVars.DEREGISTER_LINK_EN,
        MailTemplateVars.EVENT_DATES,
        MailTemplateVars.CHANGED_REGISTRATIONS,
        MailTemplateVars.CHANGED_REGISTRATIONS_EN,
        MailTemplateVars.EVALUATION_LINK,
        MailTemplateVars.EVALUATION_CODE,
        MailTemplateVars.MEETING_LINK,
        MailTemplateVars.MEETING_CODE,
        MailTemplateVars.MEETING_PASSWORD,
        MailTemplateVars.NEWSLETTER_OPTIN_LINK,
        MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN,
      ];
    case MailTemplateType.MEETING_INFOS:
    case MailTemplateType.MEETING_INFOS_2:
    case MailTemplateType.EINZELBERATUNG_MEETING_INFOS:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.MEETING_LINK,
        MailTemplateVars.MEETING_CODE,
        MailTemplateVars.MEETING_PASSWORD,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.PRODUCT_CARD,
        MailTemplateVars.PRODUCT_CARD_EN,
        MailTemplateVars.EVALUATION_LINK,
        MailTemplateVars.EVALUATION_CODE,
        MailTemplateVars.EVENT_DATES,
      ];
    case MailTemplateType.MEETING_INFOS_IN_PERSON:
    case MailTemplateType.MEETING_INFOS_2_IN_PERSON:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.APPOINTMENT_LOCATION,
        MailTemplateVars.EVENT_LOCATION,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.PRODUCT_CARD,
        MailTemplateVars.PRODUCT_CARD_EN,
        MailTemplateVars.EVALUATION_LINK,
        MailTemplateVars.EVALUATION_CODE,
        MailTemplateVars.EVENT_DATES,
      ];
    case MailTemplateType.MEETING_INFOS_HYBRID:
    case MailTemplateType.MEETING_INFOS_2_HYBRID:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.MEETING_LINK,
        MailTemplateVars.MEETING_CODE,
        MailTemplateVars.MEETING_PASSWORD,
        MailTemplateVars.APPOINTMENT_LOCATION,
        MailTemplateVars.EVENT_LOCATION,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.PRODUCT_CARD,
        MailTemplateVars.PRODUCT_CARD_EN,
        MailTemplateVars.EVALUATION_LINK,
        MailTemplateVars.EVALUATION_CODE,
        MailTemplateVars.EVENT_DATES,
      ];
    case MailTemplateType.OTP:
      return [
        MailTemplateVars.TITLE, //
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.OTP,
      ];
    case MailTemplateType.CHANGE_EMAIL:
      return [
        MailTemplateVars.TITLE, //
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.CHANGE_EMAIL_LINK,
      ];
    case MailTemplateType.FORGOT_PASSWORD:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.FORGOT_PASSWORD_LINK,
      ];
    case MailTemplateType.PRODUKT_LOESCHUNG:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.INSTITUTION_NAME,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.PRODUCT_CARD,
        MailTemplateVars.PRODUCT_CARD_EN,
      ];
    case MailTemplateType.PRODUCT_CHANGED:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.CHANGED_REGISTRATIONS,
        MailTemplateVars.CHANGED_REGISTRATIONS_EN,
        MailTemplateVars.PRODUCT_CHANGES,
        MailTemplateVars.PRODUCT_CHANGES_EN,
        MailTemplateVars.LOGIN_LINK,
      ];
    case MailTemplateType.IKH_INVOICE_USER:
    case MailTemplateType.SD_INVOICE_USER:
    case MailTemplateType.DEMO_INVOICE_USER:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.WORKSHOP_NAME,
        MailTemplateVars.WORKSHOP_TERMINE,
        MailTemplateVars.WORKSHOP_LOCATION,
        MailTemplateVars.LOGIN_LINK,
      ];
    case MailTemplateType.IKH_INVOICE_INSTITUTION:
    case MailTemplateType.SD_INVOICE_INSTITUTION:
    case MailTemplateType.DEMO_INVOICE_INSTITUTION:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.WORKSHOP_NAME,
        MailTemplateVars.WORKSHOP_TERMINE,
        MailTemplateVars.WORKSHOP_LOCATION,
        MailTemplateVars.LOGIN_LINK,
      ];
    case MailTemplateType.LECTURER_REGISTRATION:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.INSTITUTION_NAME,
      ];
    case MailTemplateType.LECTURER_ADDED_TO_PRODUCT:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.INSTITUTION_NAME,
      ];
    case MailTemplateType.INFO_NEWSLETTER:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.BOOKABLE_WORKSHOPS,
        MailTemplateVars.BOOKABLE_WORKSHOPS_EN,
        MailTemplateVars.UNSUBSCRIBE_FROM_NEWSLETTER_LINK,
      ];
    case MailTemplateType.FREE_FORM:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.INSTITUTION_NAME,
      ];
    case MailTemplateType.NEW_WORKSHOP_DOCUMENTS:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.DOCUMENT_LINKS,
      ];
    case MailTemplateType.NUTZER_WORKSHOP_SET_TEILNAHME:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.LOGIN_LINK,
        MailTemplateVars.PRODUCT_NAME,
      ];
    case MailTemplateType.LECTURER_WORKSHOP_REMINDER:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.PRODUCT_CARD,
        MailTemplateVars.EVENT_LOCATION,
        MailTemplateVars.EVENT_DATES,
        MailTemplateVars.EVENT_LEAD_DAYS,
        MailTemplateVars.EVALUATION_LINK,
        MailTemplateVars.EVALUATION_CODE,
      ];
    case MailTemplateType.LECTURER_EVALUATION_MAIL:
      return [
        MailTemplateVars.TITLE,
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.PRODUCT_CARD,
        MailTemplateVars.EVENT_LOCATION,
        MailTemplateVars.EVENT_DATES,
        MailTemplateVars.EVENT_LEAD_DAYS,
        MailTemplateVars.EVALUATION_LINK,
        MailTemplateVars.EVALUATION_CODE,
      ];
    case MailTemplateType.NICHT_ERSCHIENEN:
      return [
        MailTemplateVars.FIRST_NAME,
        MailTemplateVars.LAST_NAME,
        MailTemplateVars.PRODUCT_NAME,
        MailTemplateVars.EVENT_DATES,
      ];
    default:
      return [];
  }
}
